/** Mini tabs inside The Bridge (admin sales home). */

import {
  HARBOUR_TAB_KEY,
  HARBOUR_TAB_LABEL,
  HARBOUR_VENUE_QUERY,
} from './harbour'

export const BRIDGE_MINI_TAB_CROWS_NEST = 'crows-nest'
export const BRIDGE_MINI_TAB_CATCH_BOARD = 'catch-board'
export const BRIDGE_MINI_TAB_SPARTACUS = 'spartacus'
export const BRIDGE_MINI_TAB_HARBOUR = HARBOUR_TAB_KEY

/** Crow’s Nest → Catch Board → Spartacus → Harbour. */
export const BRIDGE_MINI_TABS = Object.freeze([
  {
    key: BRIDGE_MINI_TAB_CROWS_NEST,
    label: "Crow's Nest",
    order: 1,
  },
  {
    key: BRIDGE_MINI_TAB_CATCH_BOARD,
    label: 'Catch Board',
    order: 2,
  },
  {
    key: BRIDGE_MINI_TAB_SPARTACUS,
    label: 'Spartacus',
    order: 3,
  },
  {
    key: BRIDGE_MINI_TAB_HARBOUR,
    label: HARBOUR_TAB_LABEL,
    order: 4,
  },
])

export const BRIDGE_DEFAULT_MINI_TAB = BRIDGE_MINI_TAB_CROWS_NEST

export const BRIDGE_PAGE_TITLE = 'The Bridge'
export const BRIDGE_PAGE_SUBTITLE =
  'Sales home — Growth metrics, pipeline, and venue hunting'

/** Query param for deep-linking a mini tab (`?tab=spartacus`). */
export const BRIDGE_MINI_TAB_QUERY = 'tab'

/** Query param for a Catch Board deal (`?tab=catch-board&deal=<id>`). */
export const BRIDGE_DEAL_QUERY = 'deal'

export function isBridgeMiniTabKey(key) {
  return BRIDGE_MINI_TABS.some(tab => tab.key === key)
}

/**
 * @param {string | null | undefined} key
 * @returns {string}
 */
export function resolveBridgeMiniTab(key) {
  if (isBridgeMiniTabKey(key)) return key
  return BRIDGE_DEFAULT_MINI_TAB
}

/**
 * Build Bridge path, optionally selecting a mini tab, Catch Board deal,
 * or Harbour venue. A deal id always forces Catch Board. A venue id forces
 * Harbour unless a deal is also present.
 * @param {string} [miniTab]
 * @param {{ dealId?: string|null, venueId?: string|null }} [options]
 */
export function buildBridgeAdminPath(miniTab, { dealId, venueId } = {}) {
  const deal = String(dealId || '').trim()
  const venue = String(venueId || '').trim()
  const tab = deal
    ? BRIDGE_MINI_TAB_CATCH_BOARD
    : venue
    ? BRIDGE_MINI_TAB_HARBOUR
    : resolveBridgeMiniTab(miniTab)
  const params = new URLSearchParams()

  if (tab !== BRIDGE_DEFAULT_MINI_TAB) {
    params.set(BRIDGE_MINI_TAB_QUERY, tab)
  }
  if (deal) {
    params.set(BRIDGE_DEAL_QUERY, deal)
  } else if (venue) {
    params.set(HARBOUR_VENUE_QUERY, venue)
  }

  const query = params.toString()
  return query ? `/admin/the-bridge?${query}` : '/admin/the-bridge'
}

/**
 * Read mini-tab key from a location search string.
 * A `deal` param implies Catch Board even if `tab` is missing/wrong.
 * A `venue` param implies Harbour when there is no deal.
 * @param {string} [search]
 */
export function getBridgeMiniTabFromSearch(search = '') {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  )
  if (String(params.get(BRIDGE_DEAL_QUERY) || '').trim()) {
    return BRIDGE_MINI_TAB_CATCH_BOARD
  }
  if (String(params.get(HARBOUR_VENUE_QUERY) || '').trim()) {
    return BRIDGE_MINI_TAB_HARBOUR
  }
  return resolveBridgeMiniTab(params.get(BRIDGE_MINI_TAB_QUERY))
}

/**
 * @param {string} [search]
 * @returns {string|null}
 */
export function getCatchBoardDealIdFromSearch(search = '') {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  )
  const dealId = String(params.get(BRIDGE_DEAL_QUERY) || '').trim()
  return dealId || null
}

/**
 * @param {string} [search]
 * @returns {string|null}
 */
export function getHarbourVenueIdFromSearch(search = '') {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  )
  if (String(params.get(BRIDGE_DEAL_QUERY) || '').trim()) {
    return null
  }
  const venueId = String(params.get(HARBOUR_VENUE_QUERY) || '').trim()
  return venueId || null
}
